import { Action, AnyAction, Dispatch } from 'redux';
import { ThunkAction, ThunkDispatch } from 'redux-thunk';
import debounce from 'lodash.debounce';

import { pipe } from 'fp-ts/function';
import * as TE from 'fp-ts/TaskEither';
import * as T from 'fp-ts/Task';
import * as O from 'fp-ts/Option';
import * as A from 'fp-ts/Array';

import { FetchError } from '../../../utils/fetch';

import API from '../../common/api';
import { ApiUtils } from '../../common/utils';

import { serializeBooking } from '../utils';
import type { BusinessWorker, OrderSummary } from '../types';
import type { State } from '../reducers';

export const TOGGLE_OPTION: string = 'TOGGLE_OPTION';
export const ADD_OPTION: string = 'ADD_OPTION';
export const REMOVE_OPTION: string = 'REMOVE_OPTION';
export const CLEAR_OPTIONS: string = 'CLEAR_OPTIONS';
export const UPDATE_VOUCHER: string = 'UPDATE_VOUCHER';
export const UPDATE_BUSINESS: string = 'UPDATE_BUSINESS';
export const UPDATE_TRAVEL_TYPE: string = 'UPDATE_TRAVEL_TYPE';
export const UPDATE_CART: string = 'UPDATE_CART';
export const UPDATE_CART_SUCCESS: string = 'UPDATE_CART_SUCCESS';
export const UPDATE_CART_ERROR: string = 'UPDATE_CART_ERROR';
export const FETCH_SUMMARY: string = 'FETCH_SUMMARY';
export const FETCH_SUMMARY_SUCCESS: string = 'FETCH_SUMMARY_SUCCESS';
export const FETCH_SUMMARY_FAILURE: string = 'FETCH_SUMMARY_FAILURE';

const fetchSummarySuccess = (summary?: OrderSummary) => ({
  type: FETCH_SUMMARY_SUCCESS,
  summary,
});

const fetchSummaryFailure = (error: Error | unknown) => ({
  type: FETCH_SUMMARY_FAILURE,
  error,
});

export const fetchSummary = () => (dispatch: Dispatch) => {
  const fetchAction = dispatch({
    type: FETCH_SUMMARY,
  });

  const onUnknownRejected = (error: unknown): T.Task<Array<Action>> => T.of([dispatch(fetchSummaryFailure(error))]);

  const onFetchRejected = (error: FetchError): T.Task<Array<Action>> =>
    pipe(
      ApiUtils.handleJson(error.res),
      TE.map(err => [dispatch(fetchSummaryFailure(err))]),
      TE.getOrElse(onUnknownRejected),
    );

  const onResult = (res: Response) =>
    pipe(
      ApiUtils.handleJson(res),
      TE.flatMapIO(summary => () => [dispatch(fetchSummarySuccess(summary))]),
      TE.getOrElse(onUnknownRejected),
    );

  return pipe(
    API.getSummary(),
    TE.flatMapTask(res => (res.status === 200 ? onResult(res) : T.of([]))),
    TE.getOrElseW(onFetchRejected),
    T.map(A.prepend(fetchAction)),
  )();
};

export const removeOption = (option: number) => ({
  type: REMOVE_OPTION,
  option,
});

export const addOption = (option: number) => ({
  type: ADD_OPTION,
  option,
});

export const toggleOption =
  (option: number, previous: boolean) =>
  (dispatch: Dispatch, getState: () => State): Array<Action> => {
    // Handling exclusive categories
    const state = getState();

    const { services } = state.services;

    const findServiceById = (id: number) => services.find(s => s.id.toString() === id.toString());

    const optionService = findServiceById(option);
    if (optionService) {
      const toggleAction = dispatch({
        type: TOGGLE_OPTION,
        option,
        previous,
      });

      // Handling exclusive services
      if (optionService.isExclusiveWith) {
        const toClear = services
          .filter(s => s.id.toString() !== optionService.id.toString())
          .filter(s => s.isExclusiveWith === optionService.isExclusiveWith)
          .map(s => s.id);

        const removeActions = toClear.map(id => dispatch(removeOption(id)));
        return [toggleAction, ...removeActions];
      }

      return [toggleAction];
    }

    return [];
  };

export const clearOptions = (): Action => ({
  type: CLEAR_OPTIONS,
});

export const updateTravelType = (travelType: string, businessCustomerId?: string, worker?: BusinessWorker) => ({
  type: UPDATE_TRAVEL_TYPE,
  travelType,
  business: businessCustomerId,
  worker,
});

export const cartPersisted = (summary?: OrderSummary): AnyAction => ({
  type: UPDATE_CART_SUCCESS,
  summary,
});

export const cartPersistError = (error?: any) => ({
  type: UPDATE_CART_ERROR,
  error,
});

type AppThunkDispatch = ThunkDispatch<State, unknown, Action>;
type ThunkActionCreator = () => ThunkAction<any, State, unknown, Action>;

export const persistCart =
  (callbacksToDispatch?: Array<ThunkActionCreator>, servicesSelect?: boolean, optionsSelect?: boolean) =>
  (dispatch: AppThunkDispatch, getState: () => State): Promise<Array<Action>> => {
    const updateAction = dispatch({ type: UPDATE_CART });
    const state = getState();

    const { departure, arrival } = state.booking;
    const { options, voucher, business, worker, travelType } = state.cart;

    const { start, end } = serializeBooking(departure, arrival);

    const body = JSON.stringify({
      ...start,
      ...end,
      services: options ? options.toArray() : undefined,
      voucher,
      business,
      travelType,
      isSubstituted: worker && worker.isWorker,
      servicesSelect,
      optionsSelect,
    });

    const onUnknownRejected = (error: unknown): T.Task<Array<Action>> => T.of([dispatch(cartPersistError(error))]);

    const onFetchRejected = (error: FetchError): T.Task<Array<Action>> =>
      pipe(
        ApiUtils.handleJson(error.res),
        TE.map(err => [dispatch(cartPersistError(err))]),
        TE.getOrElse(onUnknownRejected),
      );

    const onResult = (res: Response): T.Task<Array<Action>> =>
      pipe(
        ApiUtils.handleJson(res),
        TE.map(summary =>
          pipe(
            O.fromNullable(callbacksToDispatch),
            O.map(A.map(getAction => dispatch(getAction()))),
            O.getOrElseW(() => []),
            A.append(dispatch(cartPersisted(summary))),
          ),
        ),
        TE.getOrElse(onUnknownRejected),
      );

    return pipe(
      API.saveCart(body),
      TE.flatMapTask(res => (res.status === 200 ? onResult(res) : T.of([dispatch(cartPersisted())]))),
      TE.getOrElse(onFetchRejected),
      T.map(A.prepend(updateAction)),
    )();
  };

const debouncePersist = debounce((dispatch: ThunkDispatch<State, unknown, Action>) => dispatch(persistCart()), 600);

export const updateVoucher = (voucher: string) => (dispatch: Dispatch) => {
  dispatch({
    type: UPDATE_VOUCHER,
    voucher,
  });

  // Debouncing API call
  debouncePersist(dispatch);
};
